import { PropsWithChildren, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { classNames } from "../UI/Utils/classNames.ts";
import { WeekDayEnum } from "./WeekDayEnum.tsx";
import {
  formatHour,
  WEEK_DAYS_IN_ORDER,
  weekDayShortLabels,
} from "./weeklyAvailabilityFormat.ts";

export type AvailabilityFormItem = {
  weekDay: WeekDayEnum;
  timeIntervals: { from: number; to: number }[];
};

function intervalsOf(
  availability: AvailabilityFormItem[],
  weekDay: WeekDayEnum,
): { from: number; to: number }[] {
  return availability
    .filter((a) => a.weekDay === weekDay)
    .flatMap((a) => a.timeIntervals)
    .sort((a, b) => a.from - b.from);
}

function WeeklyAvailabilitySummary({
  availability,
}: {
  availability: AvailabilityFormItem[];
}) {
  if (availability.length <= 0)
    return (
      <span className={"block text-sm text-slate-500"}>
        Nessun giorno impostato
      </span>
    );

  return (
    <span className={"grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7"}>
      {WEEK_DAYS_IN_ORDER.map((weekDay) => {
        const intervals = intervalsOf(availability, weekDay);
        return (
          <span
            key={weekDay}
            className={classNames(
              "block rounded-md px-2 py-1.5 text-xs",
              intervals.length > 0 ? "bg-slate-100" : "bg-slate-50",
            )}
          >
            <span className={"block font-semibold text-slate-700"}>
              {weekDayShortLabels[weekDay]}
            </span>
            {intervals.length > 0 ? (
              intervals.map((interval, i) => (
                <span key={i} className={"block text-slate-600"}>
                  {formatHour(interval.from)}–{formatHour(interval.to)}
                </span>
              ))
            ) : (
              <span className={"block text-slate-400"}>Non disponibile</span>
            )}
          </span>
        );
      })}
    </span>
  );
}

export function WeeklyAvailabilityCard({
  availability,
  hasErrors,
  children,
}: PropsWithChildren<{
  availability: AvailabilityFormItem[];
  hasErrors: boolean;
}>) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isOpen = isExpanded || hasErrors;

  return (
    <div
      className={classNames(
        "w-full rounded border",
        hasErrors ? "border-red-300" : undefined,
      )}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsExpanded(!isOpen)}
        className={classNames(
          "block w-full rounded p-3 text-left",
          isOpen ? undefined : "hover:bg-slate-50",
        )}
      >
        <span className={"flex items-center justify-between gap-3"}>
          <span className={"font-semibold"}>
            Disponibilità settimanale (opzionale)
          </span>
          <span className={"flex items-center gap-1 text-sm text-slate-500"}>
            {isOpen ? "Chiudi" : "Modifica"}
            <ChevronDownIcon
              className={classNames(
                "h-4 w-4 transition-transform",
                isOpen ? "rotate-180" : undefined,
              )}
            />
          </span>
        </span>
        {isOpen ? null : (
          <span className={"mt-3 block"}>
            <WeeklyAvailabilitySummary availability={availability} />
          </span>
        )}
      </button>
      {isOpen ? <div className={"px-3 pb-3"}>{children}</div> : null}
    </div>
  );
}
